import { useEffect, useMemo, useState } from "react";
import { Check, Copy, Download, ScanLine, ShieldCheck, Smartphone } from "lucide-react";
import QRCode from "qrcode";
import productImg from "@/assets/product-img.png";

const VERSION = "1.0.0";

function getDownloadUrl() {
  const basePath = window.location.pathname.replace(/\/download-app\/?$/, "");
  return `${window.location.origin}${basePath}/downloads/kyralink.apk`;
}

export default function DownloadApp() {
  const downloadUrl = useMemo(getDownloadUrl, []);
  const [qrCode, setQrCode] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    QRCode.toDataURL(downloadUrl, {
      width: 320,
      margin: 2,
      errorCorrectionLevel: "H",
      color: { dark: "#050505", light: "#ffffff" },
    }).then(setQrCode).catch(() => setQrCode(""));
  }, [downloadUrl]);

  const copyLink = async () => {
    await navigator.clipboard.writeText(downloadUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <main className="min-h-full bg-kl-black text-white kl-grain kl-sprinkle-bg" data-testid="download-app-page">
      <header className="h-16 border-b border-kl-line px-6 sm:px-10 flex items-center justify-between">
        <span className="font-display font-semibold tracking-[0.35em] text-sm">KYRALINK</span>
        <span className="kl-label-dark">Android installer · v{VERSION}</span>
      </header>

      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-12 lg:py-20 grid lg:grid-cols-[1fr_420px] gap-12 lg:gap-20 items-center">
        <section>
          <p className="kl-label-dark flex items-center gap-2"><Smartphone size={13} className="text-kl-green" /> Mobile installer</p>
          <h1 className="font-display font-light text-5xl sm:text-6xl leading-[1.02] tracking-tight mt-5">
            Set up every meter.<br />
            <span className="font-semibold">Right from your Android phone.</span>
          </h1>
          <p className="text-kl-muted max-w-xl mt-7 leading-relaxed">
            Download the Kyralink Mobile installer to pair meters over Bluetooth, configure the electrical supply, and complete customer setup on site.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <a href={downloadUrl} download className="kl-btn-primary h-12 px-6" data-testid="download-apk-button">
              <Download size={16} /> Download APK
            </a>
            <button type="button" onClick={copyLink} className="kl-btn-outline-dark h-12 px-6" data-testid="copy-apk-link">
              {copied ? <Check size={16} /> : <Copy size={16} />}{copied ? "Link copied" : "Copy download link"}
            </button>
          </div>

          <div className="mt-10 grid sm:grid-cols-3 gap-px bg-kl-line border border-kl-line max-w-2xl">
            {[
              ["01", "Download", "Open the APK from your browser."],
              ["02", "Allow", "Permit installs from this source."],
              ["03", "Install", "Launch Kyra Link and begin setup."],
            ].map(([number, title, text]) => (
              <div key={number} className="bg-kl-black p-5">
                <span className="font-mono text-kl-green text-xs">{number}</span>
                <h2 className="font-display text-lg mt-3">{title}</h2>
                <p className="text-xs text-kl-muted mt-1 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-start gap-3 text-xs text-kl-muted max-w-xl">
            <ShieldCheck size={16} className="text-kl-green shrink-0 mt-0.5" />
            <p>Android may ask you to allow installation from your browser because this installer is distributed directly instead of through Google Play.</p>
          </div>
        </section>

        <aside className="relative border border-kl-line bg-kl-surface p-6 sm:p-8">
          <div className="absolute -top-12 -right-4 w-24 h-40 hidden lg:block">
            <img src={productImg} alt="KyraLink smart energy meter" className="w-full h-full object-contain drop-shadow-[0_12px_24px_rgba(0,0,0,0.6)]" />
          </div>
          <div className="flex items-center gap-2 kl-label-dark"><ScanLine size={13} className="text-kl-green" /> Scan to download</div>
          <div className="bg-white p-3 mt-5 aspect-square flex items-center justify-center">
            {qrCode ? <img src={qrCode} alt="QR code to download Kyralink Mobile" className="w-full h-full" data-testid="apk-qr-code" /> : <span className="font-mono text-xs text-kl-black">Generating QR code…</span>}
          </div>
          <p className="font-mono text-[10px] text-kl-muted break-all mt-4 leading-relaxed">{downloadUrl}</p>
          <div className="border-t border-kl-line mt-5 pt-5 flex items-center justify-between">
            <span className="kl-label-dark">Android APK</span>
            <span className="font-mono text-xs text-kl-green">v{VERSION}</span>
          </div>
        </aside>
      </div>
    </main>
  );
}
